import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Row,
  Col,
} from "antd";

import {
  productGroupsActions,
  productBrandsActions,
  productsActions,
} from "../../../../store/slices/settings-slices";
import { productsService } from "../../../../services/settings-services";
import { getConfig } from "../../../../config";

const ProductsUpdateProductModal = ({
  setIsOpenUpdate,
  product,
  setLoadingStates,
  loadingStates,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { productGroups, trigger: groupTrigger } = useSelector(
    (store) => store.productGroups
  );
  const { productBrands, trigger: brandTrigger } = useSelector(
    (store) => store.productBrands
  );
  const { t } = useTranslation();
  const { cityCode } = getConfig();

  const {
    id,
    name,
    description,
    energy,
    proteins,
    fats,
    carbohydrates,
    price,
    groupId,
    brandId,
    excludable,
  } = product;

  useEffect(() => {
    dispatch(productGroupsActions.getAllProductGroups());
    dispatch(productBrandsActions.getAllProductBrands());
  }, [groupTrigger, brandTrigger, dispatch]);

  useEffect(() => {
    form.setFieldsValue({
      nameEn: name.en,
      nameRu: name.ru,
      nameKa: name.ge,
      nameAr: name.ar,
      descriptionEn: description.en,
      descriptionRu: description.ru,
      descriptionKa: description.ge,
      descriptionAr: description.ar,
      energy: energy || 0,
      proteins: proteins || 0,
      fats: fats || 0,
      carbohydrates: carbohydrates || 0,
      price: price || 0,
      groupId: groupId || undefined,
      brandId: brandId || undefined,
      excludable: excludable || false,
    });
  }, [
    form,
    name,
    description,
    energy,
    proteins,
    fats,
    carbohydrates,
    price,
    groupId,
    brandId,
    excludable,
  ]);

  const submitHandler = async (values) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [id]: true }));
      const formattedData = {
        name: {
          en: values.nameEn,
          ru: values.nameRu,
          ge: values.nameKa || values.nameAr,
          ar: values.nameAr || values.nameKa,
        },
        description: {
          en: values.descriptionEn || "",
          ru: values.descriptionRu || "",
          ge: values.descriptionKa || values.descriptionAr || "",
          ar: values.descriptionAr || values.descriptionKa || "",
        },
        energy: Number(values.energy) || 0,
        proteins: Number(values.proteins) || 0,
        fats: Number(values.fats) || 0,
        carbohydrates: Number(values.carbohydrates) || 0,
        price: parseFloat(values.price) || 0,
        groupId: values.groupId || null,
        brandId: values.brandId || null,
        excludable: values.excludable || false,
      };

      await productsService.updateProductById(id, formattedData);
      dispatch(productsActions.changeTrigger());
      toast.success("Product updated successfully!");
      setIsOpenUpdate(false);
    } catch (error) {
      console.error(t("modal-toast-creating-error"), error);
      toast.error(t("modal-toast-creating-error"));
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <Modal
      title={t("modal-products-update-product")}
      open={true}
      onCancel={() => setIsOpenUpdate(false)}
      onOk={() => form.submit()}
      confirmLoading={loadingStates[id]}
      okText="OK"
      cancelText="Cancel"
      width={900}
      centered
    >
      <Form form={form} layout="vertical" onFinish={submitHandler}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={`${t("modal-name-input-new-category")} (English)`}
              name="nameEn"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={`${t("modal-name-input-new-category")} (Русский)`}
              name="nameRu"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input />
            </Form.Item>

            {cityCode === "UAE" ? (
              <Form.Item
                label={`${t("modal-name-input-new-category")} (العربية)`}
                name="nameAr"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Input />
              </Form.Item>
            ) : (
              <Form.Item
                label={`${t("modal-name-input-new-category")} (ქართული)`}
                name="nameKa"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Input />
              </Form.Item>
            )}

            <Form.Item
              label={`${t("modal-Description-input")} (English)`}
              name="descriptionEn"
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={`${t("modal-Description-input")} (Русский)`}
              name="descriptionRu"
            >
              <Input />
            </Form.Item>

            {cityCode === "UAE" ? (
              <Form.Item
                label={`${t("modal-Description-input")} (العربية)`}
                name="descriptionAr"
              >
                <Input />
              </Form.Item>
            ) : (
              <Form.Item
                label={`${t("modal-Description-input")} (ქართული)`}
                name="descriptionKa"
              >
                <Input />
              </Form.Item>
            )}

            <Form.Item label="Group" name="groupId">
              <Select
                placeholder="Select a group"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {productGroups &&
                  productGroups.map((group) => (
                    <Select.Option key={group.id} value={group.id}>
                      {group.name.en}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item label="Brand" name="brandId">
              <Select
                placeholder="Select a brand"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {productBrands &&
                  productBrands.map((brand) => (
                    <Select.Option key={brand.id} value={brand.id}>
                      {brand.name?.en || brand.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={`${t("modal-energy")} (kcal)`} name="energy">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>

            <Form.Item label={`${t("modal-proteins")} (g)`} name="proteins">
              <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
            </Form.Item>

            <Form.Item label={`${t("modal-fats")} (g)`} name="fats">
              <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
            </Form.Item>

            <Form.Item
              label={`${t("modal-carbohydrates")} (g)`}
              name="carbohydrates"
            >
              <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
            </Form.Item>

            <Form.Item label={`${t("modal-price")} (₾/kg)`} name="price">
              <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
            </Form.Item>

            <Form.Item name="excludable" valuePropName="checked">
              <Checkbox>{t("modal-excludable")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export { ProductsUpdateProductModal };
